// Bank-file export modal — three paying accounts, three files.
//
// The paying account is stamped on every row of the DEFAULT BANK
// TEMPLATE, so one file can only carry one payer: SHIFTIN, Octera and
// SHIFT GROUP (Alger + Annaba, one company, one transfer per person)
// each get their own. « Marquer envoyé » stays out of reach until every
// buildable file has actually been downloaded — the failure mode this
// screen exists to prevent is a company silently left unpaid.
//
// Only VIREMENT employees go to the bank. Chèques and « payé à part »
// stay in the payroll and are listed below the payers, so the transfer
// total plus the off-file total still adds up to the month.

import { useState } from 'react';
import {
  BANK_TEMPLATE_COLUMNS,
  PAYMENT_METHODS,
  type PayrollEmployeeDTO,
  type PayrollMonthDTO,
  type PayrollPayerDTO,
} from '@pointage/shared';
import { useMarkPayrollSent } from '../../api/hooks.js';
import { toast } from '../../store/toast.js';
import { fmtDA } from '../../lib/format.js';
import { monthLabel } from './common.js';

export function BankExportModal({
  data,
  canExport,
  onClose,
}: {
  data: PayrollMonthDTO;
  canExport: boolean; // payroll.export
  onClose: () => void;
}) {
  const [mode, setMode] = useState<'sal' | 'bonus'>(data.isQuarterEnd ? 'bonus' : 'sal');
  const [downloaded, setDownloaded] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<string | null>(data.payers[0]?.id ?? null);
  const markSent = useMarkPayrollSent();

  const { year, month, quarter, isQuarterEnd, stage } = data;
  const monthName = monthLabel(month);
  const ready = stage === 'ENVOI' || stage === 'ENVOYE';
  const reference = `SALAIRE ${monthName.toUpperCase()}`;

  const amount = (e: PayrollEmployeeDTO) => e.salary + (mode === 'bonus' ? e.finalBonus ?? 0 : 0);
  const payerTotal = (p: PayrollPayerDTO) => (mode === 'bonus' ? p.totalBonus : p.total);
  const blockedReason = (p: PayrollPayerDTO): string | null => {
    if (p.count === 0) return null; // tout hors fichier : rien à envoyer, rien de bloqué
    if (!p.configured) return "Compte donneur d'ordre non renseigné (Paramètres → Paie)";
    if (p.missingBank.length > 0) {
      return `Coordonnées bancaires manquantes : ${p.missingBank.map((m) => m.name).join(', ')}`;
    }
    return null;
  };

  const buildable = data.payers.filter((p) => p.count > 0 && blockedReason(p) === null);
  const offFile = data.employees.filter((e) => e.paymentMethod !== 'VIREMENT');
  const offFileTotal = offFile.reduce((a, e) => a + amount(e), 0);
  const allDownloaded = buildable.length > 0 && buildable.every((p) => downloaded.has(p.id));
  const grandTotal = data.payers.reduce((a, p) => a + payerTotal(p), 0);
  const missingBonus = mode === 'bonus' ? data.employees.filter((e) => e.finalBonus == null).length : 0;

  const markDownloaded = (id: string) => setDownloaded((prev) => new Set(prev).add(id));
  const href = (payerId: string) =>
    `/api/payroll/bank.xlsx?year=${year}&month=${month}&mode=${mode}&payerId=${payerId}`;
  const zipHref = `/api/payroll/bank.zip?year=${year}&month=${month}&mode=${mode}`;

  const doMarkSent = async () => {
    try {
      await markSent.mutateAsync({ year, month });
      toast.success('Virements marqués envoyés — mois verrouillé.');
      onClose();
    } catch (err) {
      const e = err as Error & { body?: { message?: string } };
      toast.danger(e.body?.message ?? 'Échec du marquage envoyé.');
    }
  };

  const previewPayer = data.payers.find((p) => p.id === preview) ?? null;
  const previewTeam = previewPayer
    ? data.employees.filter((e) => previewPayer.entityIds.includes(e.entityId) && e.paymentMethod === 'VIREMENT')
    : [];

  return (
    <div className="sb-drawer-veil" onClick={onClose}>
      <div className="sb-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sb-mo-head">
          <div>
            <div className="sb-mo-title">Fichiers banque — {monthName} {year}</div>
            <div className="sb-mo-sub">
              Format <b>DEFAULT BANK TEMPLATE</b> respecté colonne par colonne · un fichier par compte payeur
            </div>
          </div>
          <button type="button" className="sb-dr-x" onClick={onClose}>✕</button>
        </div>

        <div className="sb-mo-ctrl">
          <div className="gm-seg">
            <button type="button" className={mode === 'sal' ? 'on2' : ''} onClick={() => setMode('sal')}>Salaires seuls</button>
            {isQuarterEnd && (
              <button type="button" className={mode === 'bonus' ? 'on2' : ''} onClick={() => setMode('bonus')}>
                Salaires + bonus T{quarter}
              </button>
            )}
          </div>
          <span className="sb-mo-tot gm-mono">{fmtDA(grandTotal)} DZD</span>
        </div>

        {!ready && (
          <div className="sb-mo-warn">⚠ Export verrouillé — la paie doit d'abord être validée.</div>
        )}
        {mode === 'bonus' && missingBonus > 0 && (
          <div className="sb-mo-warn">⚠ {missingBonus} bonus non arrêté{missingBonus > 1 ? 's' : ''} — ces lignes partiront au salaire seul.</div>
        )}

        <div className="sb-payers">
          {data.payers.map((p) => {
            const blocked = blockedReason(p);
            const done = downloaded.has(p.id);
            return (
              <div
                key={p.id}
                className={'sb-payer' + (preview === p.id ? ' on' : '') + (blocked ? ' blocked' : '')}
                onClick={() => setPreview(p.id)}
                data-testid={`payer-${p.id}`}
              >
                <div className="sb-payer-id">
                  <span className="sb-payer-name">{p.label}</span>
                  <span className="sb-payer-acct gm-mono">{p.configured ? p.account : '— compte non renseigné —'}</span>
                </div>
                <div className="sb-payer-num">
                  <span className="sb-payer-count">
                    {p.count} virement{p.count > 1 ? 's' : ''}
                    {p.offFileCount > 0 && ` · ${p.offFileCount} hors fichier`}
                  </span>
                  <span className="sb-payer-tot gm-mono">{fmtDA(payerTotal(p))} DA</span>
                </div>
                {blocked ? (
                  <span className="sb-payer-blocked" title={blocked}>⚠ {blocked}</span>
                ) : p.count === 0 ? (
                  <span className="sb-payer-blocked">Aucun virement</span>
                ) : canExport && ready ? (
                  <a
                    className={'sb-payer-dl' + (done ? ' done' : '')}
                    href={href(p.id)}
                    onClick={(e) => { e.stopPropagation(); markDownloaded(p.id); }}
                  >
                    {done ? '✓ téléchargé' : '↧ télécharger'}
                  </a>
                ) : (
                  <span className="sb-payer-blocked">—</span>
                )}
              </div>
            );
          })}
        </div>

        {offFile.length > 0 && (
          <div className="sb-offfile" data-testid="bank-offfile">
            <div className="sb-offfile-h">Hors fichier banque — à payer à part</div>
            {offFile.map((e) => (
              <div key={e.id} className="sb-offfile-row">
                <span>{e.name}</span>
                <span className="sb-offfile-tag">{PAYMENT_METHODS[e.paymentMethod].label}</span>
                <span className="amt gm-mono">{fmtDA(amount(e))} DA</span>
              </div>
            ))}
            <div className="sb-offfile-tot">
              <span>Total hors fichier</span>
              <span className="gm-mono">{fmtDA(offFileTotal)} DA</span>
            </div>
          </div>
        )}

        {previewPayer && (
          <div className="sb-mo-scroll">
            <table className="sb-motbl">
              <thead>
                <tr>{BANK_TEMPLATE_COLUMNS.map((c, i) => <th key={i}>{c}</th>)}</tr>
              </thead>
              <tbody>
                {previewTeam.map((e) => (
                  <tr key={e.id}>
                    <td>{previewPayer.account || '—'}</td>
                    <td>{previewPayer.swift || '—'}</td>
                    <td>{e.bankLabel ?? e.name}</td>
                    <td>{e.bankAccount ?? '—'}</td>
                    <td>DZ</td>
                    <td>{e.bankSwift ?? '—'}</td>
                    <td>DZ</td>
                    <td>OUR</td>
                    <td>DZD</td>
                    <td className="amt">{fmtDA(amount(e))}</td>
                    <td>{reference}</td>
                    <td></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="sb-mo-foot">
          <span className="sb-mo-file gm-mono">
            {stage === 'ENVOYE'
              ? '✓ Virements envoyés — mois verrouillé'
              : allDownloaded
                ? '✓ Les 3 fichiers sont sortis'
                : `${downloaded.size}/${buildable.length} fichier(s) téléchargé(s)`}
          </span>
          <div style={{ flex: 1 }} />
          {canExport && ready && buildable.length > 0 && (
            <a
              className="gm-pdf"
              style={{ textDecoration: 'none' }}
              href={zipHref}
              onClick={() => setDownloaded(new Set(buildable.map((p) => p.id)))}
            >
              ⇪ Tout télécharger (.zip)
            </a>
          )}
          {canExport && stage === 'ENVOI' && (
            <button
              type="button"
              className={'gm-submit ' + (allDownloaded ? 'ready' : 'locked')}
              onClick={allDownloaded ? doMarkSent : undefined}
              disabled={!allDownloaded || markSent.isPending}
              title={allDownloaded ? '' : 'Téléchargez les fichiers avant de marquer envoyé'}
            >
              ✓ Marquer envoyé à la banque
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
